
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CompletionMessage } from '@app-libs/model';
import { Subject, combineLatest } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { LoadingSpinnerComponent } from '../../common/components/loading-spinner';
import { ChatStateService } from '../../services/chat/chat-state.service';
import { ChatService } from '../../services/chat/chat.service';
import { ChatType } from '../../services/chat/core/chat.types';
import { MessageFormatter } from '../../services/chat/message/message-formatter.service';
import { ChatUIService } from '../../services/chat/ui/chat-ui.service';
import { ChatErrorHandler } from '../../services/chat/utils/error-handler.service';
import { ConfigurationClientService } from '../../services/client/configuration-client.service';
import { EventBusService, EventTypes } from '../../services/client/event-bus.service';
import { isMobileDevice } from '../../utils/input-handling.utils';
import { ChatHeaderComponent } from './chat-header/chat-header.component';
import { ChatInputComponent } from './chat-input/chat-input.component';
import { ChatMessagesComponent } from './chat-messages/chat-messages.component';
import { ChatScrollButtonComponent } from './chat-scroll-button/chat-scroll-button.component';

@Component({
  selector: 'app-chat',
  imports: [
    CommonModule,
    ChatMessagesComponent,
    ChatHeaderComponent,
    ChatInputComponent,
    ChatScrollButtonComponent,
    LoadingSpinnerComponent
  ],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('0.5s ease-in', style({ opacity: 1 }))]),
    ]),
  ],
})
export class ChatComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild(ChatInputComponent) chatInputComponent!: ChatInputComponent;
  @ViewChild(ChatMessagesComponent) chatMessages!: ChatMessagesComponent;
  @ViewChild('scrollContainer', { static: true })
  private scrollContainer!: ElementRef;

  private destroy$ = new Subject<void>();
  showScrollButton = false;
  loading = true;

  // Observable properties - initialized in constructor
  public readonly messages$: any;
  public readonly state$: any;
  public readonly processing$: any;
  public readonly assistant$: any;

  constructor(
    public chatService: ChatService,
    private route: ActivatedRoute,
    private eventBus: EventBusService,
    private config: ConfigurationClientService,
    private chatUIService: ChatUIService,
    private chatStateService: ChatStateService,
    private cdr: ChangeDetectorRef,
    private messageFormatter: MessageFormatter,
    private errorHandler: ChatErrorHandler,
  ) {
    // Direct observable access instead of state proxy
    this.messages$ = this.chatService.messages$;
    this.state$ = this.chatService.state$;
    this.processing$ = this.chatService.processing$;
    this.assistant$ = this.chatService.assistant$;
  }

  // Legacy state getter for backward compatibility
  get state() {
    return this.chatService.state;
  }

  ngOnInit() {
    this.chatService.resetChat();

    // Initialize chat based on route
    combineLatest([this.route.data, this.route.params])
      .pipe(takeUntil(this.destroy$))
      .subscribe(async ([data, params]) => {
        // Map route data string to ChatType enum
        const chatTypeString = data['chatType'] || 'chat';
        const chatType = this.mapStringToChatType(chatTypeString);
        const paramId = params['id'];

        // Initialize chat and trigger change detection
        await this.chatService.initChat(chatType, paramId);
        this.loading = false;
        this.cdr.detectChanges();
      });

    // Subscribe to scroll button visibility from UI service
    this.chatUIService.showScrollButton$
      .pipe(takeUntil(this.destroy$))
      .subscribe((show: boolean) => {
        this.showScrollButton = show;
        this.cdr.detectChanges();
      });

    // Handle scroll to bottom requests from ChatService
    // Only scroll if auto-scroll is enabled (to respect user's scroll position)
    this.chatService.scrollToBottom
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Check if auto-scroll is enabled before scrolling
        const scrollState = this.chatUIService.getScrollState();
        if (scrollState.autoScroll) {
          console.debug('[ChatComponent] Auto-scroll enabled, scrolling to bottom');
          this.chatUIService.scrollToBottomWithElement(this.scrollContainer);
        } else {
          console.debug('[ChatComponent] Auto-scroll disabled, NOT scrolling');
        }
      });

    // Subscribe to global events
    this.subscribeToEvent(EventTypes.SCROLL_BOTTOM, () => {
      console.debug('[ChatComponent] SCROLL_BOTTOM event received from EventBus');
      this.chatUIService.scrollToBottomWithElement(this.scrollContainer);
    });
    this.subscribeToEvent(EventTypes.SEND_USER_MESSAGE, () => {
      // When user sends a message, enable auto-scroll to see their message and the response
      this.chatUIService.setAutoScroll(true);
    });
    this.subscribeToEvent(EventTypes.NEW_CHAT, this.onNewChat.bind(this));
    this.subscribeToEvent(EventTypes.WORKSPACE_UPDATED, () => {
      // Workspace update handling
    });
    this.subscribeToEvent(EventTypes.CHAT_INITIALIZED, () => {
      // Chat initialization complete - trigger change detection
      this.cdr.detectChanges();
    });

    // Focus on input
    if (this.chatInputComponent) {
      this.chatInputComponent.focusInput();
    }

    // Subscribe to message changes for auto-scrolling during streaming
    this.messages$
      .pipe(
        takeUntil(this.destroy$),
        // Skip the first emission to avoid scrolling on initial load
        skip(1),
        // IMPORTANT: DO NOT ADD debounceTime here - it breaks the auto-scroll behavior tests
        // The ChatUIService.handleNewContent() already has its own internal debouncing
        // debounceTime(100), // DO NOT UNCOMMENT
      )
      .subscribe(() => {
        // console.debug('[ChatComponent] messages$ updated - calling handleNewContent');
        // Handle new content for scrolling
        this.chatUIService.handleNewContent();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Maps route data string to ChatType enum value
   */
  private mapStringToChatType(chatTypeString: string): ChatType {
    switch (chatTypeString) {
      case 'assistant':
        return ChatType.Assistant;
      case 'conversation':
        return ChatType.Conversation;
      case 'chat':
      default:
        return ChatType.Chat;
    }
  }

  ngAfterViewInit(): void {
    // Initialize scroll monitoring
    this.chatUIService.initScrollMonitoring(this.scrollContainer);

    // Auto-scroll if configured
    if (this.config.getSetting('chat', 'autoScrollConversation')) {
      this.chatUIService.scrollToBottomDelayed(this.scrollContainer, 1000);
    }
  }

  removeUploadedFile() {
    this.eventBus.emit({ type: EventTypes.REMOVE_UPLOADED_FILE });
  }

  onScrollToBottom(): void {
    // When user clicks scroll button, always scroll and re-enable auto-scroll
    this.chatUIService.setAutoScroll(true);
    this.chatUIService.scrollToBottomWithElement(this.scrollContainer);
  }

  onModelSelected(model: string): void {
    this.chatService.onUpdateSelectedModel(model);
  }

  sendMessageStream(userMessage: CompletionMessage): void {
    if (!userMessage.content?.trim()) {
      return;
    }

    // Validate message before sending (UI-level validation)
    const validation = this.messageFormatter.validateUIMessage(userMessage);
    if (!validation.isValid) {
      const errorMessage = validation.errors.join(', ');
      this.chatUIService.showError(`Invalid message: ${errorMessage}`);
      return;
    }

    // Show warnings if any
    if (validation.warnings.length > 0) {
      validation.warnings.forEach(warning => {
        this.chatUIService.showWarning(warning);
      });
    }

    this.chatService.sendMessageStream(userMessage).subscribe({
      next: () => {
        if (this.chatService.removeUploadedFile()) {
          this.removeUploadedFile();
        }
      },
      error: (error) => {
        // Use ChatErrorHandler for better error handling
        const chatError = this.errorHandler.handleError(error);
        const displayMessage = this.errorHandler.formatErrorForDisplay(chatError);

        this.chatUIService.showError(displayMessage);

        // Report error for monitoring
        this.errorHandler.reportError(chatError).subscribe();

        // Suggest user action if available
        const actionSuggestion = this.errorHandler.getUserActionSuggestion(chatError);
        if (actionSuggestion) {
          setTimeout(() => {
            this.chatUIService.showInfo(actionSuggestion);
          }, 1000);
        }
      },
    });

    // Ensure input stays focused after sending
    setTimeout(() => {
      if (this.chatInputComponent && !isMobileDevice()) {
        this.chatInputComponent.focusInput();
      }
    }, 100);
  }

  onNewChat(): void {
    const savedAssistantId = this.chatStateService.currentState.assistant.assistant_id;
    const selectedModel = this.chatStateService.currentState.selectedModel;

    this.chatService.resetChat();
    this.chatStateService.selectedModel = selectedModel;

    setTimeout(() => {
      this.chatService.initChat(ChatType.Assistant, savedAssistantId);
    }, 500);

    this.chatUIService.showSuccess('Started new chat');
  }

  private subscribeToEvent(eventType: EventTypes, handler: (data?: any) => void): void {
    this.eventBus
      .on(eventType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => handler(event.data));
  }
}
